// src/lib/calculator/calculator-storage.ts
//
// SSR-safe localStorage helpers for the peptide calculator. NEVER throws — every
// read/write is guarded against missing `window`, quota errors, and corrupt JSON.
//
// Deliberate omission: syringe size is NEVER persisted or restored. It always
// resets to the 1.0 ml barrel on load so the visual syringe never silently shows
// a stale, undersized barrel for a freshly-restored protocol.

import type { DisplayUnit, DoseUnitMode, SavedProtocolInput, SavedProtocolRecord } from "./types";

// ─── Keys ────────────────────────────────────────────────────────────────────

export const BB_PR_PREFS = "bb-pr-prefs-v2";
export const BB_PR_HISTORY = "bb-pr-history-v1";
export const BB_PR_PROTOCOLS = "bb-pr-protocols-v1";

/**
 * Same-tab change signal for the history store. The native `storage` event only
 * fires in OTHER tabs, so {@link pushHistory} dispatches this on `window` after a
 * same-tab write to let live surfaces (the HistoryStrip's useSyncExternalStore)
 * re-snapshot.
 */
export const HISTORY_EVENT = "bb-pr-history";

const HISTORY_MAX = 6;
/** Guest-fallback cap — mirrors the account-path limit so both surfaces behave alike. */
export const GUEST_PROTOCOLS_MAX = 10;

// ─── Generic JSON helpers ──────────────────────────────────────────────────────

/** Reads + parses a JSON value from localStorage. Returns `fallback` on SSR, missing key, or bad JSON. */
export function loadJSON<T>(key: string, fallback: T): T {
	if (typeof window === "undefined") return fallback;
	try {
		const raw = window.localStorage.getItem(key);
		if (raw == null) return fallback;
		return JSON.parse(raw) as T;
	} catch {
		return fallback;
	}
}

/** Serializes + writes a JSON value to localStorage. No-op on SSR or quota/serialization error. */
export function saveJSON(key: string, val: unknown): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(key, JSON.stringify(val));
	} catch {
		// swallow quota / serialization errors — persistence is best-effort
	}
}

// ─── Calculator preferences ────────────────────────────────────────────────────

/**
 * Persisted calculator preferences. NOTE: intentionally has NO `syringeSize` —
 * the barrel must always reset to 1.0 ml on load (see file header).
 */
export interface CalculatorPrefs {
	peptideSlug: string;
	peptideAmount: number;
	bacWater: number;
	dose: number;
	calibration: 40 | 100;
	displayUnit: DisplayUnit;
	doseUnitMode: DoseUnitMode;
}

export function loadPrefs(): CalculatorPrefs | null {
	return loadJSON<CalculatorPrefs | null>(BB_PR_PREFS, null);
}

/**
 * Persists prefs. Defensively strips any syringe-related field so a syringe size
 * can never leak into storage even if callers pass an over-broad object.
 */
export function savePrefs(prefs: CalculatorPrefs): void {
	// Destructure away anything syringe-shaped, keep only the known-safe fields.
	const { peptideSlug, peptideAmount, bacWater, dose, calibration, displayUnit, doseUnitMode } =
		prefs as CalculatorPrefs & { syringeSize?: unknown; syringe?: unknown };
	const safe: CalculatorPrefs = {
		peptideSlug,
		peptideAmount,
		bacWater,
		dose,
		calibration,
		displayUnit,
		doseUnitMode,
	};
	saveJSON(BB_PR_PREFS, safe);
}

// ─── Calculator history ────────────────────────────────────────────────────────

export interface HistoryEntry {
	/** Dedupe signature: `${peptideSlug}|${vial}|${bac}|${dose}`. */
	sig: string;
	peptideSlug: string;
	peptideName: string;
	peptideAmount: number;
	bacWater: number;
	dose: number;
	/** Pre-formatted dose label for display (unit-aware). */
	doseDisplay: string;
	iu: number;
	mode: string;
}

export function loadHistory(): HistoryEntry[] {
	const raw = loadJSON<HistoryEntry[]>(BB_PR_HISTORY, []);
	return Array.isArray(raw) ? raw : [];
}

/**
 * Prepends `entry` to the history, dedupes by `sig` (the new entry wins), keeps
 * newest first, and caps the list at {@link HISTORY_MAX}.
 */
export function pushHistory(entry: HistoryEntry): HistoryEntry[] {
	const existing = loadHistory().filter((e) => e.sig !== entry.sig);
	const next = [entry, ...existing].slice(0, HISTORY_MAX);
	saveJSON(BB_PR_HISTORY, next);
	// Signal same-tab listeners (the cross-tab `storage` event won't fire here).
	// Guarded against environments where dispatchEvent/Event aren't available (jsdom-less tests).
	if (
		typeof window !== "undefined" &&
		typeof window.dispatchEvent === "function" &&
		typeof Event === "function"
	) {
		window.dispatchEvent(new Event(HISTORY_EVENT));
	}
	return next;
}

/** Builds the dedupe signature for a history entry. */
export function historySig(peptideSlug: string, vial: number, bac: number, dose: number): string {
	return `${peptideSlug}|${vial}|${bac}|${dose}`;
}

// ─── Guest protocols (localStorage fallback) ─────────────────────────────────
//
// When the visitor is NOT signed in, protocols live in localStorage under
// {@link BB_PR_PROTOCOLS} instead of the account API. Records share the
// {@link SavedProtocolRecord} shape so the UI can treat guest + account paths
// uniformly; guest records carry a synthetic `userId: "guest"`. Capped at
// {@link GUEST_PROTOCOLS_MAX}. This module ONLY ever touches its own key — it
// never enumerates or deletes foreign localStorage entries.

/**
 * Returns true for a value that structurally looks like a saved protocol record.
 *
 * Two valid shapes (discriminated by `kind`, default "single"):
 *  - `single` — the classic calculator snapshot; REQUIRES numeric `dose` + `syringeSize`.
 *  - `titration` — a multi-week plan; has a schedule (derived from the peptide +
 *    anmix at render time), so it carries NO single `dose`/`syringeSize` and those
 *    fields are NOT required.
 *
 * Both shapes always carry id/name/peptideSlug/peptideAmount/bacWater. This lets a
 * titration plan and a single protocol coexist in the same guest list without the
 * filter dropping the titration one (the original data-loss bug).
 */
function isProtocolRecord(value: unknown): value is SavedProtocolRecord {
	if (typeof value !== "object" || value === null) return false;
	const r = value as Record<string, unknown>;
	const commonOk =
		typeof r.id === "string" &&
		typeof r.name === "string" &&
		typeof r.peptideSlug === "string" &&
		typeof r.peptideAmount === "number" &&
		typeof r.bacWater === "number";
	if (!commonOk) return false;
	// `kind` is optional; absent/"single" is the classic shape, "titration" is the plan.
	const kind = r.kind === "titration" ? "titration" : "single";
	if (kind === "titration") return true;
	return typeof r.dose === "number" && typeof r.syringeSize === "number";
}

/** Loads the guest protocol list. Returns `[]` on SSR, missing key, or corrupt JSON. */
export function loadGuestProtocols(): SavedProtocolRecord[] {
	const raw = loadJSON<unknown>(BB_PR_PROTOCOLS, []);
	if (!Array.isArray(raw)) return [];
	return raw.filter(isProtocolRecord);
}

/** Generates a collision-resistant id for a guest protocol (crypto.randomUUID with a fallback). */
function makeGuestId(): string {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return `guest-${crypto.randomUUID()}`;
	}
	return `guest-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Saves a new guest protocol (newest first), caps the list at
 * {@link GUEST_PROTOCOLS_MAX}, and returns the updated list. Accepts BOTH a
 * `single` snapshot (with dose + syringeSize) and a `titration` plan (with a
 * startDate, no single dose) — `kind` is normalized so every persisted record
 * carries an explicit discriminant. When the list is already full the input is
 * dropped and the existing list is returned unchanged (the UI hides the save
 * action at the cap, so this is a defensive guard).
 */
export function saveGuestProtocol(input: SavedProtocolInput): SavedProtocolRecord[] {
	const existing = loadGuestProtocols();
	if (existing.length >= GUEST_PROTOCOLS_MAX) return existing;
	const now = new Date().toISOString();
	const record: SavedProtocolRecord = {
		...input,
		kind: input.kind ?? "single",
		id: makeGuestId(),
		userId: "guest",
		createdAt: now,
		updatedAt: now,
	};
	const next = [record, ...existing].slice(0, GUEST_PROTOCOLS_MAX);
	saveJSON(BB_PR_PROTOCOLS, next);
	return next;
}

/** Updates the name/notes of a guest protocol by id and returns the updated list. */
export function updateGuestProtocol(
	id: string,
	patch: { name: string; notes?: string },
): SavedProtocolRecord[] {
	const next = loadGuestProtocols().map((p) =>
		p.id === id ? { ...p, name: patch.name, notes: patch.notes, updatedAt: new Date().toISOString() } : p,
	);
	saveJSON(BB_PR_PROTOCOLS, next);
	return next;
}

/** Deletes a guest protocol by id and returns the updated list. */
export function deleteGuestProtocol(id: string): SavedProtocolRecord[] {
	const next = loadGuestProtocols().filter((p) => p.id !== id);
	saveJSON(BB_PR_PROTOCOLS, next);
	return next;
}
